import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { SearchSelect, type SSOption } from "../ui/SearchSelect";
import { trpc } from "../../lib/trpc";
import { parseTrNumber } from "../../lib/trNumber";
import "./PriceAgreementDrawer.css";

const CURRENCIES = ["TRY", "USD", "EUR", "GBP"] as const;
type Currency = (typeof CURRENCIES)[number];

interface Props {
  open: boolean;
  customerId: string;
  customerName?: string;
  /** Önseçili ürün (sipariş satırından açılınca kilitli gösterilir). */
  itemKind?: "stock" | "semi" | "finished";
  itemId?: string;
  itemLabel?: string;
  /** Revize modu — formu mevcut anlaşma değerleriyle ön-doldurur. */
  initialPrice?: number | null;
  initialCurrency?: string | null;
  initialRate?: number | null;
  onClose: () => void;
  onCreated?: () => void;
}

const todayStr = (): string => {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

function fmtTl(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
}

/** Revize ön-doldurma: TR biçimli, binliksiz ("34,5") — parseTrNumber ile birebir geri okunur. */
function toTrInput(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "";
  return new Intl.NumberFormat("tr-TR", { useGrouping: false, maximumFractionDigits: 6 }).format(n);
}

/** Para girişi: yalnız rakam / nokta / virgül kalsın (TR klavyede ondalık tuşu virgül). */
const sanitizeDecimal = (v: string): string => v.replace(/[^\d.,]/g, "");

/**
 * Para alanını oku — boş → null; geçersiz ya da ≤0 → NaN; geçerli → pozitif sayı.
 * Number(x) / x.replace(",", ".") KULLANILMAZ: "34,50" ve "1.234,56" ancak parseTrNumber ile doğru okunur.
 */
function readPositive(raw: string): number | null {
  if (!raw.trim()) return null;
  const n = parseTrNumber(raw);
  return Number.isFinite(n) && n > 0 ? n : NaN;
}

/**
 * Cascade drawer — bir müşteri+ürün için fiyat anlaşması oluşturur.
 * Sipariş veya müşteri detay drawer'ının ÜZERİNE açılır (Drawer stack).
 */
export function PriceAgreementDrawer({ open, customerId, customerName, itemKind, itemId, itemLabel, initialPrice, initialCurrency, initialRate, onClose, onCreated }: Props) {
  const isRevise = initialPrice != null;
  const qc = useQueryClient();
  const utils = trpc.useUtils();
  const lockedItem = !!itemId;
  const [productId, setProductId] = useState(itemId ?? "");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState<Currency>("TRY");
  const [rate, setRate] = useState("");
  const [agreedAt, setAgreedAt] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [priceErr, setPriceErr] = useState<string | null>(null);
  const [rateErr, setRateErr] = useState<string | null>(null);

  // Mamul seçici: TÜM aktif mamuller (v_items) — pagination YOK. (Eski finishedProduct.list pageSize:200,
  // ListInput max(100) sınırını aşıp 400 veriyordu → liste hiç yüklenmiyordu.)
  const itemOptsQ = trpc.inventory.itemOptions.useQuery(undefined, { enabled: open && !lockedItem, staleTime: 60_000 });
  const createMut = trpc.priceAgreement.create.useMutation();

  useEffect(() => {
    if (!open) return;
    setProductId(itemId ?? "");
    // TR biçimli (virgüllü) yaz — gönderimdeki parseTrNumber ile tutarlı.
    setPrice(toTrInput(initialPrice));
    setCurrency(((initialCurrency as Currency) || "TRY"));
    setRate(toTrInput(initialRate));
    setAgreedAt(todayStr());
    setNotes("");
    setErr(null);
    setPriceErr(null);
    setRateErr(null);
    createMut.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemId, initialPrice, initialCurrency, initialRate]);

  const isForeign = currency !== "TRY";
  // Sadece mamuller (kind === "finished"); ad = etiket, kod = mono tail — ikisi de aranır.
  const productOptions = useMemo<SSOption[]>(
    () => ((itemOptsQ.data ?? []) as Array<{ id: string; kind: string; code: string; name: string }>)
      .filter((o) => o.kind === "finished")
      .map((o) => ({ value: o.id, label: o.name, tail: o.code })),
    [itemOptsQ.data],
  );
  const mutErr = createMut.error?.message ?? null;

  // ≈TL önizlemesi — gönderimle AYNI ayrıştırıcı (parseTrNumber); geçersiz girdide gösterilmez.
  const pricePreview = readPositive(price);
  const ratePreview = readPositive(rate);
  const tlPreview =
    isForeign && pricePreview != null && ratePreview != null && Number.isFinite(pricePreview) && Number.isFinite(ratePreview)
      ? pricePreview * ratePreview
      : null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const pid = lockedItem ? itemId! : productId;
    if (!pid) return setErr("Ürün seçin.");
    // Para alanları parseTrNumber ile okunur; geçersizse kayıt DURUR + alanın altında inline hata (sessiz 0/null YOK).
    const p = readPositive(price);
    const r = isForeign ? readPositive(rate) : null;
    const pErr = p == null ? "Birim fiyat zorunlu." : Number.isNaN(p) ? "Geçerli bir birim fiyat girin (örn. 34,50)." : null;
    const rErr = !isForeign
      ? null
      : r == null
        ? "Döviz seçtiniz — kur (TRY karşılığı) zorunlu."
        : Number.isNaN(r)
          ? "Geçerli bir kur girin (örn. 34,50)."
          : null;
    setPriceErr(pErr);
    setRateErr(rErr);
    if (pErr || rErr || p == null) return;
    try {
      await createMut.mutateAsync({
        customerId,
        itemKind: (lockedItem ? itemKind : "finished") ?? "finished",
        itemId: pid,
        unitPrice: p,
        currency,
        exchangeRate: isForeign ? r : null,
        agreedAt: agreedAt ? new Date(agreedAt).toISOString() : undefined,
        notes: notes || undefined,
      });
      // Yalnız anlaşma sorgularını BEKLE (sipariş editörü güncel anlaşmayı görsün); gerisi arka planda
      // tazelenir. Filtresiz await, tüm aktif sorgular dönene dek "Kaydediliyor…"yu kilitliyordu (mobil ağ).
      await utils.priceAgreement.invalidate();
      void qc.invalidateQueries({
        predicate: (q) => !(Array.isArray(q.queryKey[0]) && q.queryKey[0][0] === "priceAgreement"),
      });
      onCreated?.();
      onClose();
    } catch {
      /* mutErr banner */
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="md"
      title={isRevise ? "Anlaşmayı Revize Et" : "Yeni Fiyat Anlaşması"}
      subtitle={isRevise ? "Yeni güncel anlaşma olarak kaydedilir" : customerName ? `Müşteri: ${customerName}` : undefined}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={createMut.isPending}>İptal</button>
          <button type="submit" form="pa-form" className="btn-md primary" disabled={createMut.isPending}>
            {createMut.isPending ? "Kaydediliyor…" : "Anlaşmayı Kaydet"}
          </button>
        </>
      }
    >
      <form id="pa-form" className="pa-form" onSubmit={submit} noValidate>
        {err || mutErr ? <div className="form-banner-err" role="alert">{err ?? mutErr}</div> : null}

        <div className="form-field">
          <label className="lbl" id="pa-item-lbl" htmlFor={lockedItem ? undefined : "pa-item"}>Ürün <span className="req">*</span></label>
          {lockedItem ? (
            // Kilitli ürün: satır kaydıran salt-okunur blok (disabled input uzun adı sağdan kesiyordu).
            <div id="pa-item" className="pa-locked" role="textbox" aria-readonly="true" aria-labelledby="pa-item-lbl" tabIndex={0}>
              {itemLabel ?? "(seçili ürün)"}
            </div>
          ) : (
            <SearchSelect
              id="pa-item"
              value={productId || null}
              options={productOptions}
              // Ürün seçilince "Ürün seçin." bandı kalksın — fiyat/kur hataları da yazınca temizleniyor.
              onChange={(v) => { setProductId(v ?? ""); setErr(null); }}
              placeholder="— Mamul seç —"
              searchPlaceholder="Mamul adı / kodu ara…"
              emptyText={itemOptsQ.isLoading ? "Yükleniyor…" : "Mamul bulunamadı"}
            />
          )}
        </div>

        <div className="form-row pa-price-row">
          <div className="form-field">
            <label className="lbl" htmlFor="pa-price">Birim Fiyat <span className="req">*</span></label>
            <input
              id="pa-price"
              className={`form-input pa-num${priceErr ? " err" : ""}`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={price}
              onChange={(e) => { setPrice(sanitizeDecimal(e.target.value)); setPriceErr(null); }}
              placeholder="örn. 34,50"
              aria-invalid={priceErr ? true : undefined}
              aria-describedby={priceErr ? "pa-price-err" : undefined}
              required
            />
            {priceErr ? <span id="pa-price-err" className="err" role="alert">{priceErr}</span> : null}
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="pa-cur">Para Birimi</label>
            <select id="pa-cur" className="form-select" value={currency} onChange={(e) => { setCurrency(e.target.value as Currency); setRateErr(null); }}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="pa-rate">Kur (1 {currency} = ? ₺){isForeign ? <span className="req"> *</span> : null}</label>
            <input
              id="pa-rate"
              className={`form-input pa-num${rateErr ? " err" : ""}`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={rate}
              onChange={(e) => { setRate(sanitizeDecimal(e.target.value)); setRateErr(null); }}
              disabled={!isForeign}
              placeholder={isForeign ? "örn. 34,50" : "TL'de gerekmez"}
              aria-invalid={rateErr ? true : undefined}
              aria-describedby={rateErr ? "pa-rate-err" : tlPreview != null ? "pa-rate-tl" : undefined}
            />
            {rateErr ? <span id="pa-rate-err" className="err" role="alert">{rateErr}</span> : null}
            {!rateErr && tlPreview != null ? (
              <span id="pa-rate-tl" className="help mono">≈ {fmtTl(tlPreview)} ₺ (birim, kura göre)</span>
            ) : null}
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="pa-date">Anlaşma Tarihi</label>
            <input id="pa-date" className="form-input" type="date" value={agreedAt} onChange={(e) => setAgreedAt(e.target.value)} />
            <span className="help">"Son anlaşma" bu tarihe göre belirlenir.</span>
          </div>
        </div>

        <div className="form-field">
          <label className="lbl" htmlFor="pa-notes">Not</label>
          <textarea id="pa-notes" className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} rows={2} />
        </div>
      </form>
    </Drawer>
  );
}
